import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { styled } from '@mui/system';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MainCard from 'ui-component/cards/MainCard';

import SearchIcon from '@mui/icons-material/Search';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Checkbox, FormControlLabel, Box, IconButton } from "@mui/material";
// project imports

import { gridSpacing } from 'store/constant';
import { getCityStateList } from 'api_handlers/discovery';
import { BC_COUNTRIES } from 'constants/bcCountries';
import { API_AGE_TO_DISPLAY, DEFAULT_BC_AGE_OPTIONS } from 'constants/bcAgeGroups';

import AdvancedFilters from './AdvancedFilters';
import PredictionModal from './PredictionModal';
import AppliedFilters from './AppliedFilters';
import SearchAutocomplete from './SearchAutocomplete';
import SocialMediaButton from '../Extra/SocialMediaIcon';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import Face6Icon from '@mui/icons-material/Face6';
import TimelineIcon from '@mui/icons-material/Timeline';
import LunchDiningIcon from '@mui/icons-material/LunchDining';

import { fetchInfluencersAction, setAutoCompleteValues } from 'actions/planner2';
import { updateFilters, resetFilters } from 'actions/planner-filters';
import { getFieldOfInterest } from 'api_handlers/influencer';
import { getPlannerUniqueES } from 'api_handlers/es';
import FilterListIcon from '@mui/icons-material/FilterList';
import WcIcon from '@mui/icons-material/Wc';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import mixpanel from '../../../../utils/mixpanel';
import { InputAdornment, OutlinedInput, CircularProgress } from '@mui/material';

// ==============================|| PLUGIN - AUTOCOMPLETE ||============================== //

const ResizableTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  transition: 'width 0.3s ease',
  '&:focus-within': {
    width: '100%',
    zIndex: 9999,
  },
}));

const DEFAULT_WATCHING_INTERESTS = ["30 Minute Chefs",
  "Action & Adventure Movie Fans",
  "Action Game Fans",
  "Adventure & Strategy Game Fans",
  "Art & Theater Aficionados",
  "Aspiring Chefs",
  "Audiophiles",
  "Auto Enthusiasts",
  "Avid Business News Readers",
  "Avid Investors",
  "Avid Local News Readers",
  "Avid News Readers",
  "Avid Political News Readers",
  "Avid World News Readers",
  "Banking & Finance",
  "Banks Online",
  "Bargain Hunters",
  "Baseball Fans",
  "Basketball Fans",
  "Beachbound Travelers",
  "Beauty & Wellness",
  "Beauty Mavens",
  "Blues Fans",
  "Boating & Sailing Enthusiasts",
  "Book Lovers",
  "Business Professionals",
  "Business Travelers",
  "Casual & Social Gamers",
  "Cat Lovers",
  "Charitable Donors & Volunteers",
  "Classical Music Enthusiasts",
  "Cloud Services Power Users",
  "Coffee Shop Regulars",
  "Comedy Movie Fans",
  "Comics & Animation Fans",
  "Console Gamers",
  "Convenience Store Shoppers",
  "Cooking Enthusiasts",
  "Cord Cutters",
  "Cord Nevers",
  "Country Music Fans",
  "Credit Card Preferences",
  "Cricket Enthusiasts",
  "Cycling Enthusiasts",
  "Department Store Shoppers",
  "Diners by Meal",
  "Do-It-Yourselfers",
  "Documentary & Nonfiction TV Fans",
  "Dog Lovers",
  "Driving & Racing Game Fans",
  "Electronic Dance Music Fans",
  "Entertainment News Enthusiasts",
  "Esports Fans",
  "Family Movie Fans",
  "Family Television Fans",
  "Family Vacationers",
  "Family-Focused",
  "Fans of American Football",
  "Fans of Australian Football",
  "Fans of Latin Music",
  "Fans of New & Upcoming Movies",
  "Fans of New & Upcoming Video Games",
  "Fans of South Asian Film",
  "Fashionistas",
  "Fast Food Cravers",
  "Fight & Wrestling Fans",
  "Folk & Traditional Music Enthusiasts",
  "Food & Dining",
  "Foodies",
  "Frequently Attends Live Events",
  "Frequently Dines Out",
  "Frequently Eats Breakfast Out",
  "Frequently Eats Dinner Out",
  "Frequently Eats Lunch Out",
  "Frequently Visits Salons",
  "Game, Reality & Talk Show Fans",
  "Gamers",
  "Golf Enthusiasts",
  "Green Living Enthusiasts",
  "Grocery Shoppers",
  "Hardcore Gamers",
  "Health & Fitness Buffs",
  "High-End Computer Aficionados",
  "Hockey Fans",
  "Home & Garden",
  "Home Automation Enthusiasts",
  "Home Decor Enthusiasts",
  "Homeschooling Parents",
  "Horror Movie Fans",
  "Indie & Alternative Rock Fans",
  "Jazz Enthusiasts",
  "Lifestyles & Hobbies",
  "Light TV Viewers",
  "Live Sports Viewers",
  "Luxury Shoppers",
  "Luxury Travelers",
  "Media & Entertainment",
  "Men's Media Fans",
  "Metalheads",
  "Mobile Enthusiasts",
  "Motor Sports Enthusiasts",
  "Motorcycle Enthusiasts",
  "Movie Lovers",
  "Music Lovers",
  "News & Politics",
  "Nightlife Enthusiasts",
  "Olympics Fans",
  "Outdoor Enthusiasts",
  "PC Gamers",
  "Performance & Luxury Vehicle Enthusiasts",
  "Pet Lovers",
  "Pop Music Fans",
  "Prefers Organic Food",
  "Prioritizes Vehicle Fuel Efficiency",
  "Prioritizes Vehicle Performance",
  "Public Transit Users",
  "Racquetball Enthusiasts",
  "Rap & Hip Hop Fans",
  "Rock Music Fans",
  "Roleplaying Game Fans",
  "Romance & Drama Movie Fans",
  "Rugby Enthusiasts",
  "Running Enthusiasts",
  "Sci-Fi & Fantasy Movie Fans",
  "Sci-Fi & Fantasy TV Fans",
  "Shooter Game Fans",
  "Shoppers",
  "Shoppers by Store Type",
  "Shopping Enthusiasts",
  "Shutterbugs",
  "Skiing Enthusiasts",
  "Snowbound Travelers",
  "Soccer Fans",
  "Social Media Enthusiasts",
  "Sports & Fitness",
  "Sports Fans",
  "Sports Game Fans",
  "Superstore Shoppers",
  "Superstore Shoppers (Offline)",
  "Swimming Enthusiasts",
  "TV Comedy Fans",
  "TV Drama Fans",
  "TV Lovers",
  "Taxi Service Users",
  "Technology",
  "Technophiles",
  "Tennis Enthusiasts",
  "Thrill Seekers",
  "Transportation Modes",
  "Travel",
  "Travel Buffs",
  "Truck & SUV Enthusiasts",
  "Value Shoppers",
  "Values Cash Back Offers",
  "Values Points & Miles",
  "Vegans",
  "Vegetarians & Vegans",
  "Vehicle Buying Preferences",
  "Vehicles & Transportation",
  "Viewership Behavior",
  "Water Sports Enthusiasts",
  "Weightlifters",
  "Winter Sports Enthusiasts",
  "Women's Media Fans",
  "World Music Fans",
  "Yoga Lovers"
];

// ---- BC Advanced Filters ----

const Search = (props) => {
  const { aiSearchLoading = false } = props;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.planner2.user);
  const predictionStatus = useSelector((state) => state.planner2.predictionStatus);
  const autoCompleteValues = useSelector((state) => state.planner2.autoCompleteValues);
  const plannerFilters = useSelector((state) => state.plannerFilters);

  const gender = useSelector((state) => state.plannerFilters.gender || []);
  const platform = useSelector((state) => state.plannerFilters.platform);
  const creatorType = useSelector((state) => state.plannerFilters.creatorType || []);
  const locations = useSelector((state) => state.plannerFilters.locations);

  const [locationOptions, setLocationOptions] = React.useState([]);
  const [, setSelectedFilter] = useState(0);
  const [aiModeFilter, setAiModeFilter] = useState(false);

  const [value, setValue] = useState('');
  const [enhancedSearch, setEnhancedSearch] = useState(false);
  const aiSearchInputRef = useRef(null);

  // Advanced filter states
  const [openAdvancedFilters, setOpenAdvancedFilters] = React.useState(false);
  // Predictions Modal
  const [openPredictions, setOpenPredictions] = React.useState(false);
  const [, setCategoryOptions] = React.useState([]);

  // const [bcRosterOnly, setBcRosterOnly] = useState(false);
  const bcRosterOnly = useSelector((state) => state.plannerFilters.bcRosterOnly || "no");
  const setBcRosterOnly = (value) => {
    dispatch(updateFilters('bcRosterOnly', value, true));
  };
  // const [insightsSharedOnly, setInsightsSharedOnly] = useState('all');
  const insightsSharedOnly = useSelector((state) => state.plannerFilters.insightsSharedOnly || 'all');
  const [bcUniqueOptions, setBcUniqueOptions] = useState({
    watchingInterests: [],
    shoppingInterests: [],
    ageOptions: DEFAULT_BC_AGE_OPTIONS,
    countryOptions: [],
  });

  // const [autoCompleteValues, setAutoCompleteValues] = React.useState([]);

  const setFilters = () => {
    const postsText = autoCompleteValues.find((option) => option.type === 'PostSearch');
    if (postsText) dispatch(updateFilters('posts', postsText.value, false));
    else dispatch(updateFilters('posts', null, false));

    const bioText = autoCompleteValues.find((option) => option.type === 'BioSearch');
    if (bioText) dispatch(updateFilters('bio', bioText.value, false));
    else dispatch(updateFilters('bio', null, false));

    const categories = autoCompleteValues
      .filter((option) => option.group === 'Categories')
      .map((option) => option.name)
      .join(',');
    if (categories && categories.length > 0) dispatch(updateFilters('category', categories, false));
    else dispatch(updateFilters('category', null, false));

    const influencerIds = autoCompleteValues
      .filter((option) => option.group === 'Influencers' && option.type === 'influencers-found')
      .map((option) => option.id);
    if (influencerIds.length > 0) dispatch(updateFilters('ids', influencerIds, false));
    else dispatch(updateFilters('ids', null, false));

    // const newInfluencers = autoCompleteValues
    //   .filter((option) => option.group === 'Influencers' && option.type === 'influencer-not-found')
    //   .map((option) => option.id);

    // dispatch(updateNewInfluencerSearch(newInfluencers));

    dispatch(fetchInfluencersAction(1));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    dispatch(fetchInfluencersAction(1));

    try {
      const resp = await getCityStateList('city');
      setLocationOptions(resp.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  }, [dispatch]);
  useEffect(() => {
    setFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCompleteValues]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getFieldOfInterest();
        const categories = data.map((option) => option.name);
        console.log('Categories: ', categories);
        setCategoryOptions(categories);
      } catch (error) {
        console.error('Error fetching field of interest:', error);
      }
    };
    fetchData();
  }, []);

  const memoizedValue = useMemo(() => (locations ? locations : []), [locations]);

  // useEffect(() => {
  //   console.log("Planner filters changed", plannerFilters);
  //   let values = [...autoCompleteValues]
  //   if(!plannerFilters.posts && values.find((option) => option.type === "PostSearch")) {
  //     values = values.filter((option) => option.type !== "PostSearch");
  //   }

  //   if(!plannerFilters.bio && values.find((option) => option.type === "BioSearch")) {
  //     values = values.filter((option) => option.type !== "BioSearch");
  //   }

  //   if(!plannerFilters.category && values.find((option) => option.group === "Categories")) {
  //     values = values.filter((option) => option.group !== "Categories");
  //   }

  //   if(!plannerFilters.ids && values.find((option) => option.group === "Influencers")) {
  //     values = values.filter((option) => option.group !== "Influencers");
  //   }
  //   // setAutoCompleteValues(values);
  // }, [plannerFilters]);

  useEffect(() => {
    props.plannerFilter(plannerFilters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannerFilters]);

  const [minSubscribers, setMinSubscribers] = useState('');
  const [maxSubscribers, setMaxSubscribers] = useState('');

  // Fetch BC unique options when BC roster is enabled (YouTube only)
  useEffect(() => {
    if (platform === 'youtube' && bcRosterOnly === 'yes') {
      getPlannerUniqueES()
        .then((data) => {
          const watchingInterests = data?.bc_audience_watch_interest_max || [];
          const shoppingInterests = data?.bc_audience_shopping_interest_max || [];
          // Keep the raw ES bucket key (e.g. "age18To24") as the option value — AudienceFilters
          // maps it to a display label via API_AGE_TO_DISPLAY, but the dispatched filter value
          // must stay in the raw form the backend's `terms` query expects.
          const ageOptions = (data?.audience_age_distribution_max || [])
            .filter((key) => API_AGE_TO_DISPLAY[key]);
          const countryCodes = data?.audience_country_distribution_max || [];
          const countryOptions = countryCodes
            .map((code) => BC_COUNTRIES[code])
            .filter(Boolean)
            .sort();
          setBcUniqueOptions({
            watchingInterests: watchingInterests.length ? watchingInterests : DEFAULT_WATCHING_INTERESTS,
            shoppingInterests: shoppingInterests.length ? shoppingInterests : [],
            ageOptions: ageOptions.length ? ageOptions : DEFAULT_BC_AGE_OPTIONS,
            countryOptions,
          });
        })
        .catch(() => {
          setBcUniqueOptions({
            watchingInterests: DEFAULT_WATCHING_INTERESTS,
            shoppingInterests: [],
            ageOptions: DEFAULT_BC_AGE_OPTIONS,
            countryOptions: Object.values(BC_COUNTRIES).sort(),
          });
        });
    }
  }, [platform, bcRosterOnly]);

  // Clear location and BC filters when BC roster toggle changes (different filter systems)
  useEffect(() => {
    dispatch(updateFilters('locations', null, true));
    dispatch(updateFilters('watchingInterest', null, false));
    dispatch(updateFilters('shoppingInterest', null, false));
    dispatch(updateFilters('audienceAge', null, false));
  }, [bcRosterOnly, dispatch]);

  // Focus AI search input when user switches to AI mode
  useEffect(() => {
    if (aiModeFilter && aiSearchInputRef.current) {
      const timer = setTimeout(() => aiSearchInputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [aiModeFilter]);

  return (
    <MainCard
      sx={{
        background: props.v2 ? '#ffffff' : '#fcf8ef',
        ...(props.v2 && {
          border: '1px solid #e9eaf0',
          borderRadius: '14px',
          boxShadow: '0 4px 18px -14px rgba(17,25,54,0.4)',
        }),
      }}
    >
      <Grid container spacing={gridSpacing}>
        <Grid item width={'100%'}>
          <Grid container direction={'column'} justifyContent={'center'}>
            {!props.v2 && (
              <Grid item>
                <Typography
                  sx={{
                    fontSize: '45px',
                    fontWeight: 600,
                    color: '#f9b715',
                  }}
                >
                  Discover Influencers for your Brand
                </Typography>
              </Grid>
            )}
            {!props.v2 && (
              <Grid item>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '15px',
                    mt: '5px',
                  }}
                >
                  Discover from 1M+ influencers across India on YouTube and Instagram
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item width={'100%'}>
          {user.is_staff ? (
            <Grid
              container
              alignItems="center"
              justifyContent={props.v2 ? 'space-between' : 'center'}
              sx={{ position: 'relative' }}
            >
              <Grid
                item
                sx={{
                  width: props.v2 ? '250px' : '280px',
                  height: props.v2 ? '30px' : '40px',
                  background: props.v2 ? '#eef1f6' : '#fff',
                  border: props.v2 ? '1px solid #e3e6ee' : 'none',
                  textAlign: 'center',
                  borderRadius: '50px',
                }}
              >
                <Grid
                  container
                  width={'100%'}
                  height={'100%'}
                  justifyContent={'center'}
                  alignItems={'center'}
                >
                  <Grid
                    item
                    xs={6}
                    md={6}
                    height={'100%'}
                    sx={{
                      cursor: 'pointer',
                      background: aiModeFilter ? '#fff' : '#f9b715',
                      border: aiModeFilter ? '' : props.v2 ? '1px solid #e0a912' : '2px solid #000',
                      textAlign: 'center',
                      alignContent: 'center',
                      borderRadius: '50px',
                    }}
                    onClick={() => {
                      // if (aiModeFilter) {
                      //   mixpanel.track('planner_discover', {
                      //     action: 'ai_mode',
                      //     data: { mode: 'manual' },
                      //   });
                      // }
                      setAiModeFilter(false);
                      props.aiMode(false);
                    }}
                  >
                    <Typography sx={{ fontWeight: '600', ...(props.v2 && { fontSize: 12, whiteSpace: 'nowrap' }) }}>
                      Manual Filters
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    md={6}
                    height={'100%'}
                    width={'100%'}
                    sx={{
                      cursor: 'pointer',
                      background: aiModeFilter ? '#f9b715' : '#fff',
                      border: aiModeFilter ? (props.v2 ? '1px solid #e0a912' : '2px solid #000') : '',
                      textAlign: 'center',
                      alignContent: 'center',
                      borderRadius: '50px',
                    }}
                    onClick={() => {
                      // if (!aiModeFilter) {
                      //   mixpanel.track('planner_discover', {
                      //     action: 'ai_mode',
                      //     data: { mode: 'ai' },
                      //   });
                      // }
                      setAiModeFilter(true);
                      props.aiMode(true);
                    }}
                  >
                    <Grid container justifyContent={'center'} alignItems={'center'} spacing={props.v2 ? 0.5 : 1} sx={{}}>
                      <Grid item>
                        <AutoGraphIcon
                          sx={props.v2 ? { width: '16px', height: '16px', display: 'block' } : { width: '20px', mt: '5px' }}
                        ></AutoGraphIcon>
                      </Grid>
                      <Grid item>
                        <Typography
                          sx={{
                            height: '100%',
                            alignContent: 'center',
                            fontWeight: '600',
                            ...(props.v2 && { fontSize: 12, px: '4px', whiteSpace: 'nowrap' }),
                          }}
                        >
                          {`AI Mode (Beta)`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {props.v2 && aiModeFilter ? (
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={enhancedSearch}
                        onChange={(event) => setEnhancedSearch(event.target.checked)}
                      />
                    }
                    label={<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Enhanced Search</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Grid>
              ) : null}
            </Grid>
          ) : (
            ''
          )}
        </Grid>

        {aiModeFilter === false ? (
          <Grid container sx={{ marginTop: '0px' }}>
            <Grid item xs={12} md={12} className={props.v2 ? 'dv2-filter-row' : undefined} sx={{ mt: props.v2 ? '12px' : '20px' }}>
              <Grid container spacing={1} alignItems={'center'} justifyContent={'space-between'}>
                <Grid
                  item
                  xs={11}
                  // sx={{ border: '2px solid #000', borderRadius: 2, background: '#f8fafc' }}
                >
                  <Grid container>
                    <Grid item xs={2}>
                      <FormControl
                        fullWidth
                        className={props.v2 ? 'dv2-platform' : undefined}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            ...(props.v2 && { minHeight: 50, height: 50 }),
                            '& fieldset': {
                              borderColor: '#000',
                            },
                          },
                          '& fieldset': {
                            borderRight: 'none',
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            ...(props.v2 ? {} : { height: 61 }),
                          },
                          '& MuiSelect-icon': {
                            top: 'calc(50% - 0.6em)',
                          },
                        }}
                      >
                        <InputLabel id="platform-select">Platform</InputLabel>
                        <Select
                          labelId="platform-select"
                          id="platform"
                          name="platform"
                          value={platform}
                          onChange={(event) => {
                            if (process.env.NODE_ENV === 'production') {
                              mixpanel.track('platform_changed', {
                                type: event.target.value,
                              });
                            }
                            dispatch(updateFilters('platform', event.target.value, true));
                            props.platform(event.target.value);
                          }}
                          label="Platform"
                        >
                          <MenuItem value={'instagram'}>
                            <Grid container spacing={1}>
                              <Grid item>
                                <SocialMediaButton platform="instagram" />
                              </Grid>
                              <Grid item>
                                <Typography
                                  sx={{ fontWeight: 800, fontSize: '0.8rem', marginTop: '2px' }}
                                >
                                  Instagram
                                </Typography>
                              </Grid>
                            </Grid>
                          </MenuItem>
                          <MenuItem value={'youtube'}>
                            <Grid container spacing={1}>
                              <Grid item>
                                <SocialMediaButton platform="youtube" />
                              </Grid>
                              <Grid item>
                                <Typography
                                  sx={{ fontWeight: 800, fontSize: '0.8rem', marginTop: '2px' }}
                                >
                                  YouTube
                                </Typography>
                              </Grid>
                            </Grid>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={10}>
                      <div className={props.v2 ? 'dv2-search' : undefined} style={{ width: '100%' }}>
                        <SearchAutocomplete v2={props.v2} />
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
                {/* <Grid item xs={'auto'}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SearchIcon sx={{color: 'primary.main'}} />}
                onClick={setFilters}
                style={{
                  height: 40,
                }}
              >
                <Typography variant="h5" color="primary" fontWeight={600}>Search</Typography>
              </Button>
            </Grid> */}
                <Grid item xs={'auto'}>
                  <Button
                    variant={props.v2 ? 'outlined' : 'text'}
                    color="secondary"
                    startIcon={props.v2 ? <RestartAltIcon sx={{ fontSize: 18 }} /> : undefined}
                    onClick={() => {
                      console.log('Resetting filters');
                      dispatch(resetFilters());
                      dispatch(setAutoCompleteValues([]));
                    }}
                    sx={
                      props.v2
                        ? {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 13,
                            color: '#5b6478',
                            borderColor: '#dcdfe8',
                            borderRadius: '10px',
                            height: 40,
                            px: 1.75,
                            '&:hover': { borderColor: '#b9bed0', background: '#f7f8fb' },
                          }
                        : undefined
                    }
                  >
                    {props.v2 ? (
                      'Reset'
                    ) : (
                      <Typography variant="h5" color="secondary" fontWeight={600} sx={{ opacity: 0.5 }}>
                        Reset
                      </Typography>
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={12} sx={{ marginTop: '24px' }}>
              <Grid container spacing={1} alignItems={'center'} justifyContent={'left'}>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Grid container spacing={1} alignItems={'center'} justifyContent={'left'}>
                    <Grid item xs={2}>
                      <Autocomplete
                        multiple
                        size="small"
                        value={memoizedValue}
                        options={bcRosterOnly === 'yes' ? (bcUniqueOptions.countryOptions.length ? bcUniqueOptions.countryOptions : Object.values(BC_COUNTRIES).sort()) : locationOptions}
                        renderTags={(values) => (
                          <>
                            <span
                              style={{
                                backgroundColor: 'rgb(197, 210, 255)',
                                borderRadius: '4px',
                                color: 'rgb(18, 25, 38)',
                                fontSize: '0.5rem',
                                paddingLeft: '1.5%',
                                paddingRight: '1.5%',
                              }}
                            >
                              {values[0].length > 7 ? `${values[0].substring(0, 7)}...` : values[0]}
                            </span>
                            {values.length > 1 ? (
                              <span
                                style={{
                                  backgroundColor: 'rgb(197, 210, 255)',
                                  borderRadius: '4px',
                                  color: 'rgb(18, 25, 38)',
                                  fontSize: '0.5rem',
                                  marginLeft: '2.5%',
                                  paddingLeft: '1.5%',
                                  paddingRight: '1.5%',
                                }}
                              >
                                {'+' + (values.length - 1)}
                              </span>
                            ) : null}
                          </>
                        )}
                        renderInput={(params) => (
                          <ResizableTextField
                            label="Location"
                            {...params}
                            placeholder={
                              props.v2 && (!memoizedValue || memoizedValue.length === 0)
                                ? 'Select location'
                                : undefined
                            }
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  {props.v2 ? (
                                    <LocationOnIcon sx={{ fontSize: 18, color: '#9aa1b2', ml: 0.5 }} />
                                  ) : null}
                                  {params.InputProps?.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        onChange={(event, value) => {
                          if (value && value.length > 0)
                            dispatch(updateFilters('locations', value, true));
                          else dispatch(updateFilters('locations', null, true));
                        }}
                        limitTags={0}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#000',
                            },
                          },
                          // border: '1px solid #000',
                          borderRadius: 2,
                          height: '42px',
                          '& fieldset': { height: '47px', top: '-5px' },
                        }}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#000',
                            },
                          },
                          borderRadius: 2,
                        }}
                      >
                        <InputLabel id="gender-select">Gender</InputLabel>
                        <Select
                          multiple
                          labelId="gender-select"
                          id="gender"
                          name="gender"
                          value={gender}
                          displayEmpty={props.v2}
                          renderValue={
                            props.v2
                              ? (sel) =>
                                  !sel || sel.length === 0 ? (
                                    <span style={{ color: '#9aa1b2' }}>All genders</span>
                                  ) : (
                                    sel.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')
                                  )
                              : undefined
                          }
                          startAdornment={
                            props.v2 ? (
                              <InputAdornment position="start">
                                <WcIcon sx={{ fontSize: 18, color: '#9aa1b2' }} />
                              </InputAdornment>
                            ) : null
                          }
                          onChange={(event) => {
                            // setGender(event.target.value);
                            dispatch(updateFilters('gender', event.target.value, true));
                            // if (event.target.value)
                            //   dispatch(updateFilters('gender', event.target.value, true));
                            // else dispatch(updateFilters('gender', null, true));
                          }}
                          label="Platform"
                        >
                          {bcRosterOnly === 'yes' ? [
                            <MenuItem key="male" value={'male'}>Male</MenuItem>,
                            <MenuItem key="female" value={'female'}>Female</MenuItem>,
                            <MenuItem key="userSpecified" value={'userSpecified'}>User Specified</MenuItem>,
                          ] : [
                            <MenuItem key="male" value={'male'}>Male</MenuItem>,
                            <MenuItem key="female" value={'female'}>Female</MenuItem>,
                            <MenuItem key="other" value={'other'}>Other</MenuItem>,
                            <MenuItem key="page" value={'page'}>Page</MenuItem>,
                          ]}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={2}>
                      {bcRosterOnly === 'yes' ? (
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <TextField
                              label="Min Subs"
                              size="small"
                              fullWidth
                              value={minSubscribers}
                              onChange={(e) => {
                                setMinSubscribers(e.target.value);
                                dispatch(updateFilters('minSubscribers', e.target.value || null, true));
                              }}
                              type="number"
                              sx={{
                                '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#000' } },
                                borderRadius: 2,
                              }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Max Subs"
                              size="small"
                              fullWidth
                              value={maxSubscribers}
                              onChange={(e) => {
                                setMaxSubscribers(e.target.value);
                                dispatch(updateFilters('maxSubscribers', e.target.value || null, true));
                              }}
                              type="number"
                              sx={{
                                '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#000' } },
                                borderRadius: 2,
                              }}
                            />
                          </Grid>
                        </Grid>
                      ) : (
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#000',
                            },
                          },

                          borderRadius: 2,
                        }}
                      >
                        <InputLabel id="platform-select">Creator Type</InputLabel>
                        <Select
                          multiple
                          labelId="platform-select"
                          id="platform"
                          name="platform"
                          value={creatorType}
                          displayEmpty={props.v2}
                          renderValue={
                            props.v2
                              ? (sel) =>
                                  !sel || sel.length === 0 ? (
                                    <span style={{ color: '#9aa1b2' }}>All creator types</span>
                                  ) : (
                                    sel.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')
                                  )
                              : undefined
                          }
                          startAdornment={
                            props.v2 ? (
                              <InputAdornment position="start">
                                <GroupsIcon sx={{ fontSize: 18, color: '#9aa1b2' }} />
                              </InputAdornment>
                            ) : null
                          }
                          onChange={(event) => {
                            dispatch(updateFilters('creatorType', event.target.value, true));
                          }}
                          label="Creator Type"
                          fullWidth
                        >
                          <MenuItem value={'mega'}>{`Mega ( > 1M)`}</MenuItem>
                          <MenuItem value={'macro'}>{`Macro (100K - 1M)`}</MenuItem>
                          <MenuItem value={'micro'}>{`Micro (10K - 100K)`}</MenuItem>
                          <MenuItem value={'nano'}>{`Nano ( < 10K)`}</MenuItem>
                        </Select>
                      </FormControl>
                      )}
                    </Grid>

                    {/* <Grid item xs={2}>
                  <Autocomplete
                    multiple
                    size="small"
                    value={categories}
                    options={categoryOptions}
                    renderInput={(params) => <ResizableTextField label="Category" {...params} />}
                    onChange={(event, value) => {
                      if (value && value.length > 0)
                        dispatch(
                          updateFilters(
                            'categories',
                            value,
                            true,
                          ),
                        );
                      else dispatch(updateFilters('categories', null, true));
                    }}
                    limitTags={0}
                    fullWidth
                    sx={{'& fieldset': {height: '47px', top: '-7px'}}}
                  />
                </Grid> */}

                  {/* Insights Shared Only — visible only for YouTube BC Roster */}
                    {platform === 'youtube' && bcRosterOnly === "yes" && (
                    <Grid item xs="auto" >
                    <FormControl
                      size="small"
                      // sx={{
                      //   '& .MuiOutlinedInput-root': {
                      //     '& fieldset': {
                      //       borderColor: '#000',
                      //     },
                      //   },
                      //   borderRadius: 2,
                      // }}
                      sx={{
                        minWidth: 180,
                        height: 40,
                        px: 1,
                        // border: '1px solid #000',
                                                background: '#f8fafc',
                        justifyContent: 'center',

                        // 🔥 remove inner outlined border
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#000',
                          },
                        },
                        borderRadius: 2,
                      }}
                    >
                      <InputLabel id="insights-select-label">Insights Shared</InputLabel>
                      <Select
                        labelId="insights-select-label"
                        id="insights-shared-only"
                        name="insights-shared-only"
                        value={insightsSharedOnly}
                        label="Insights Shared"
                        onChange={(e) => {
                          const value = e.target.value;
                          // setInsightsSharedOnly(value);
                          dispatch(updateFilters('insightsSharedOnly', value, true));
                        }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="yes">Enabled</MenuItem>
                        <MenuItem value="no">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                    </Grid>
                    )}

                    <Grid item xs={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          background: '#f8fafc',
                          border: '1px solid #000',
                          boxShadow: 'none',
                          '&:hover': { backgroundColor: '#f8fafc', boxShadow: 'none' },
                        }}
                        disableRipple
                        fullWidth
                        onClick={() => setOpenAdvancedFilters(true)}
                        style={{ height: 40, borderRadius: 7 }}
                      >
                        <FilterListIcon sx={{ fontSize: '20px', marginRight: '10px' }} />
                        <Typography variant="h5" color="secondary" fontWeight={600}>
                          Advanced Filters
                        </Typography>
                      </Button>
                    </Grid>
                
                    {predictionStatus === 'loaded' && (
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          startIcon={<WorkspacePremiumIcon sx={{ color: 'primary.main' }} />}
                          onClick={() => setOpenPredictions(true)}
                          style={{
                            height: 40,
                            borderRadius: 7,
                          }}
                        >
                          <Typography variant="h5" color="primary" fontWeight={600}>
                            Predictions
                          </Typography>
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>


            <AppliedFilters v2={props.v2} selectedFilter={async (val) => await setSelectedFilter(val)} />
          </Grid>
        ) : (
          <Grid container>
            <Grid item xs={12} md={12} className={props.v2 ? 'dv2-filter-row' : undefined} sx={{ mt: props.v2 ? '12px' : '20px' }}>
              <Grid container spacing={1} alignItems={'center'} justifyContent={'space-between'}>
                <Grid
                  item
                  xs={11}
                  // sx={{ border: '2px solid #000', borderRadius: 2, background: '#f8fafc' }}
                >
                  <Grid container>
                    <Grid item xs={2}>
                      <FormControl
                        fullWidth
                        className={props.v2 ? 'dv2-platform' : undefined}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            ...(props.v2 && { minHeight: 50, height: 50 }),
                            '& fieldset': {
                              borderColor: '#000',
                            },
                          },
                          '& fieldset': {
                            borderRight: 'none',
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            ...(props.v2 ? {} : { height: 56 }),
                          },
                          '& MuiSelect-icon': {
                            top: 'calc(50% - 0.6em)',
                          },
                        }}
                      >
                        <InputLabel id="platform-select">Platform</InputLabel>
                        <Select
                          labelId="platform-select"
                          id="platform"
                          name="platform"
                          value={platform}
                          onChange={(event) => {
                            if (process.env.NODE_ENV === 'production') {
                              mixpanel.track('platform_changed', {
                                type: event.target.value,
                              });
                            }
                            dispatch(updateFilters('platform', event.target.value, true));
                            props.platform(event.target.value);
                          }}
                          label="Platform"
                        >
                          <MenuItem value={'instagram'}>
                            <Grid container spacing={1}>
                              <Grid item>
                                <SocialMediaButton platform="instagram" />
                              </Grid>
                              <Grid item>
                                <Typography
                                  sx={{ fontWeight: 800, fontSize: '0.8rem', marginTop: '2px' }}
                                >
                                  Instagram
                                </Typography>
                              </Grid>
                            </Grid>
                          </MenuItem>
                          <MenuItem value={'youtube'}>
                            <Grid container spacing={1}>
                              <Grid item>
                                <SocialMediaButton platform="youtube" />
                              </Grid>
                              <Grid item>
                                <Typography
                                  sx={{ fontWeight: 800, fontSize: '0.8rem', marginTop: '2px' }}
                                >
                                  YouTube
                                </Typography>
                              </Grid>
                            </Grid>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={10}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        className={props.v2 ? 'dv2-search dv2-ai-search' : undefined}
                      >
                        {props.v2 && (
                          <InputLabel htmlFor="ai-search-input">
                            Describe the influencers you need
                          </InputLabel>
                        )}
                        <OutlinedInput
                          id="ai-search-input"
                          fullWidth
                          label={props.v2 ? 'Describe the influencers you need' : undefined}
                          placeholder={props.v2 ? undefined : 'Describe the influencers you need'}
                          value={value}
                          inputRef={aiSearchInputRef}
                          disabled={aiSearchLoading}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !aiSearchLoading) {
                              event.preventDefault();
                              // if (aiModeFilter) {
                              //   const q = (value || '').trim();
                              //   mixpanel.track('planner_discover', {
                              //     action: 'ai_search',
                              //     data: {
                              //       platform,
                              //       has_query: Boolean(q),
                              //       query_length: q.length,
                              //       source: 'enter',
                              //     },
                              //   });
                              // }
                              props.onSearch(value, enhancedSearch);
                            }
                          }}
                          onChange={(event) => {
                            setValue(event.target.value);
                          }}
                          sx={
                            props.v2
                              ? {
                                  pr: 0.5,
                                  '& fieldset': {
                                    borderLeft: 'none',
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                  },
                                }
                              : {
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#000',
                                  },
                                  '& fieldset': {
                                    borderColor: '#000fff',
                                    borderLeft: 'none',
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                  },
                                  '& .MuiOutlinedInput-input::placeholder': {
                                    color: '#0f0f0f',
                                    opacity: 0.7,
                                  },
                                }
                          }
                          endAdornment={
                            <InputAdornment position="end">
                              {aiSearchLoading ? (
                                <CircularProgress size={24} sx={{ mr: 0.5 }} />
                              ) : (
                                <IconButton
                                  onClick={async () => {
                                    // if (aiModeFilter) {
                                    //   const q = (value || '').trim();
                                    //   mixpanel.track('planner_discover', {
                                    //     action: 'ai_search',
                                    //     data: {
                                    //       platform,
                                    //       has_query: Boolean(q),
                                    //       query_length: q.length,
                                    //       source: 'button',
                                    //     },
                                    //   });
                                    // }
                                    props.onSearch(value, enhancedSearch);
                                  }}
                                  disabled={aiSearchLoading}
                                  aria-label="Search"
                                  sx={
                                    props.v2
                                      ? {
                                          background: '#111936',
                                          borderRadius: '10px',
                                          width: 40,
                                          height: 40,
                                          '&:hover': { background: '#1b2550' },
                                        }
                                      : undefined
                                  }
                                >
                                  <SearchIcon sx={props.v2 ? { color: '#febd1c' } : undefined} />
                                </IconButton>
                              )}
                            </InputAdornment>
                          }
                        />
                      </FormControl>
                      {/* <TextField
                        id="outlined-basic"
                        placeholder={"Describe the influencers you need"}
                        variant="outlined"
                        fullWidth
                        value={value}
                        onChange={(event)=>{
                          setValue(event.target.value)
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#000',
                            },
                          },
                          '& fieldset': {
                            borderColor: '#000fff',
                            borderLeft: 'none',
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                          },
                          

                        }}
                      /> */}
                    </Grid>
                  </Grid>
                </Grid>
                {/* <Grid item xs={'auto'}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SearchIcon sx={{color: 'primary.main'}} />}
                onClick={setFilters}
                style={{
                  height: 40,
                }}
              >
                <Typography variant="h5" color="primary" fontWeight={600}>Search</Typography>
              </Button>
            </Grid> */}
              </Grid>
            </Grid>
            {/* Enhanced Search moved into the Manual/AI mode toggle row above (right-aligned) */}
            {props.v2 ? (
              <Grid item xs={12} md={12} sx={{ marginTop: '14px' }}>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#6b7280', mb: 1 }}>
                  Describe your ideal creator in the search bar above - or try a sample prompt:
                </Typography>
                <Grid container spacing={1.25}>
                  {[
                    { icon: '💄', text: 'Beauty influencers in Mumbai', bg: '#FFEAF1' },
                    { icon: '📈', text: 'Tech creators with high ER', bg: '#E9F8EE' },
                    { icon: '🍔', text: 'Food influencers from tier 1 cities', bg: '#FFF1E6' },
                    { icon: '💪', text: 'Fitness creators in Delhi', bg: '#E8F2FF' },
                    { icon: '✈️', text: 'Travel vloggers with high reach', bg: '#F4ECFF' },
                    { icon: '🎮', text: 'Gaming streamers under 100K', bg: '#FFF7DB' },
                    { icon: '👗', text: 'Sustainable fashion creators', bg: '#E6FAF6' },
                    { icon: '🎵', text: 'Music artists with viral reels', bg: '#FFF1E6' },
                  ].map((s) => (
                    <Grid item key={s.text}>
                      <Box
                        onClick={() => setValue(s.text)}
                        sx={{
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          height: '36px',
                          pl: '6px',
                          pr: '14px',
                          borderRadius: '999px',
                          background: '#fff',
                          border: '1px solid #e3e6ee',
                          transition: 'all .15s ease',
                          '&:hover': {
                            borderColor: '#cfd4e2',
                            boxShadow: '0 3px 10px -5px rgba(17,25,54,0.3)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '8px',
                            background: s.bg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                          }}
                        >
                          {s.icon}
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#2b3245' }}>
                          {s.text}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ) : (
            <Grid item xs={12} md={12} sx={{marginTop:'24px'}}>
              <Grid container sx={{ marginLeft: '40px' }} spacing={2}>
                <Grid
                  item
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setValue('Beauty influencers in Mumbai');
                  }}
                >
                  <Grid
                    container
                    justifyContent={'center'}
                    alignItems={'center'}
                    sx={{
                      height: '30px',
                      background: '#fff',
                      alignContent: 'center',
                      paddingLeft: '15px',
                      paddingRight: '15px',
                      borderRadius: '50px',
                      border: '1px solid #000',
                    }}
                  >
                    <Grid item>
                      <Face6Icon sx={{ width: '18px', mt: '4px' }}></Face6Icon>
                    </Grid>
                    <Grid item sx={{ ml: '10px' }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: '600' }}>
                        Beauty influencers in Mumbai
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid
                    container
                    justifyContent={'center'}
                    alignItems={'center'}
                    sx={{
                      height: '30px',
                      background: '#fff',
                      alignContent: 'center',
                      paddingLeft: '15px',
                      paddingRight: '15px',
                      borderRadius: '50px',
                      border: '1px solid #000',
                      cursor: 'pointer',
                    }}
                  >
                    <Grid item>
                      <TimelineIcon sx={{ width: '18px', mt: '4px' }}></TimelineIcon>
                    </Grid>
                    <Grid item sx={{ ml: '10px' }}>
                      <Typography
                        sx={{ fontSize: '14px', fontWeight: '600' }}
                        onClick={() => {
                          setValue('Tech creators with high ER');
                        }}
                      >
                        Tech creators with high ER
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid
                    container
                    justifyContent={'center'}
                    alignItems={'center'}
                    sx={{
                      height: '30px',
                      background: '#fff',
                      alignContent: 'center',
                      paddingLeft: '15px',
                      paddingRight: '15px',
                      borderRadius: '50px',
                      border: '1px solid #000',
                      cursor: 'pointer',
                    }}
                  >
                    <Grid item>
                      <LunchDiningIcon sx={{ width: '18px', mt: '6px' }}></LunchDiningIcon>
                    </Grid>
                    <Grid item sx={{ ml: '10px' }}>
                      <Typography
                        sx={{ fontSize: '14px', fontWeight: '600' }}
                        onClick={() => {
                          setValue('Food influencers from tier 1 cities');
                        }}
                      >
                        {/* Sustainable fashion micro-influencer */}
                        Food influencers from tier 1 cities
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            )}
          </Grid>
        )}
      </Grid>
      <AdvancedFilters v2={props.v2} open={openAdvancedFilters} setOpen={setOpenAdvancedFilters} bcRosterOnly={bcRosterOnly} setBcRosterOnly={setBcRosterOnly} bcUniqueOptions={bcUniqueOptions} platform={platform} />
      <PredictionModal open={openPredictions} setOpen={setOpenPredictions} />
    </MainCard>
  );
};

export default Search;
